// Relative date presets for the table date filter ("Today", "This week", ...).
//
// A preset filter is stored as `{ operator, preset: "<id>" }` with empty `from`/`to`, so a
// saved filter or view keeps meaning "today" rather than the date it was created on. Call
// `resolveDateFilterValue` wherever the dates are actually needed (API query, client-side
// predicate) to turn the preset into concrete dates for the moment it is evaluated.

const toDateStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const DATE_PRESETS = [
  {
    id: "today",
    label: "Today",
    getRange: () => {
      const str = toDateStr(new Date());
      return { operator: "is", from: str, to: str };
    },
  },
  {
    id: "yesterday",
    label: "Yesterday",
    getRange: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const str = toDateStr(d);
      return { operator: "is", from: str, to: str };
    },
  },
  {
    id: "thisWeek",
    label: "This week",
    getRange: () => {
      const now = new Date();
      const dayOfWeek = now.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(now);
      monday.setDate(now.getDate() + mondayOffset);
      return {
        operator: "between",
        from: toDateStr(monday),
        to: toDateStr(now),
      };
    },
  },
  {
    id: "thisMonth",
    label: "This month",
    getRange: () => {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        operator: "between",
        from: toDateStr(firstDay),
        to: toDateStr(lastDay),
      };
    },
  },
];

export const getDatePreset = (id) =>
  id ? DATE_PRESETS.find((p) => p.id === id) : undefined;

// Builds the stored value for a preset: no concrete dates, just the preset id.
export const datePresetValue = (preset) => ({
  operator: preset.getRange().operator,
  from: "",
  to: "",
  preset: preset.id,
});

// Returns a value with concrete `from`/`to` for right now. Non-preset values pass through.
export const resolveDateFilterValue = (value) => {
  const preset = getDatePreset(value?.preset);
  if (!preset) return value;
  return { ...value, ...preset.getRange() };
};
